import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listAdminPosts, deletePost } from "@/lib/admin-blog.functions";

export const Route = createFileRoute("/_authenticated/admin/blog/")({
  head: () => ({
    meta: [
      { title: "Blogg — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BlogList,
});

function BlogList() {
  const fn = useServerFn(listAdminPosts);
  const delFn = useServerFn(deletePost);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "blog"],
    queryFn: () => fn(),
  });

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Radera "${title}"?`)) return;
    await delFn({ data: { id } });
    qc.invalidateQueries({ queryKey: ["admin", "blog"] });
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Blogg</h1>
          <p className="text-sm text-white/50">{data?.posts.length ?? 0} inlägg</p>
        </div>
        <Link
          to="/admin/blog/$id"
          params={{ id: "new" }}
          className="rounded-full bg-mint px-4 py-2 text-sm font-medium text-mint-foreground"
        >
          + Nytt inlägg
        </Link>
      </header>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/50">
            <tr>
              <th className="text-left px-4 py-3">Titel</th>
              <th className="text-left px-4 py-3">Kategori</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">EN</th>
              <th className="text-left px-4 py-3">Publicerad</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading && <tr><td colSpan={6} className="px-4 py-6 text-white/50">Laddar…</td></tr>}
            {!isLoading && (data?.posts ?? []).length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-white/50">Inga inlägg.</td></tr>
            )}
            {(data?.posts ?? []).map((p) => (
              <tr key={p.id} className="hover:bg-white/[0.03]">
                <td className="px-4 py-3">
                  <Link to="/admin/blog/$id" params={{ id: p.id }} className="text-white hover:underline">
                    <span className="mr-2">{p.cover_emoji}</span>{p.title}
                  </Link>
                  <div className="text-xs text-white/40">/{p.slug}</div>
                </td>
                <td className="px-4 py-3 text-white/70">{p.category}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs rounded-full px-2 py-0.5 ${
                    p.status === "published" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/60"
                  }`}>{p.status}</span>
                </td>
                <td className="px-4 py-3 text-white/60 text-xs">{p.translation_status_en}</td>
                <td className="px-4 py-3 text-white/60 text-xs">
                  {new Date(p.published_at).toLocaleDateString("sv-SE")}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleDelete(p.id, p.title)}
                    className="text-xs text-rose-300 hover:text-rose-200"
                  >
                    Radera
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
